// Mini-selector "Con / Sin marca de agua" que aparece al descargar cuando la
// marca tiene una config activa. Desktop: card centrada; mobile: bottom sheet.
// Un tap = elegir y seguir la descarga; tocar el fondo = cancelar la descarga.

import { createPortal } from 'react-dom';
import { Check, Image as ImageIcon, Stamp } from 'lucide-react';
import { useIsDesktop } from '../../pages/edicionGrafica/lib/useIsDesktop';
import {
  getLastWatermarkChoice,
  type WatermarkChoice,
} from '../../utils/watermarkConfig';

interface Props {
  open: boolean;
  onPick: (c: WatermarkChoice) => void;
  onClose: () => void;
}

export function WatermarkDownloadChooser({ open, onPick, onClose }: Props) {
  const isDesktop = useIsDesktop();
  if (!open) return null;
  const last = getLastWatermarkChoice();

  const option = (choice: WatermarkChoice, label: string, Icon: typeof Stamp) => {
    const selected = last === choice;
    return (
      <button
        type="button"
        onClick={() => onPick(choice)}
        className={`h-12 w-full flex items-center gap-3 px-3.5 rounded-md text-left transition ${
          selected
            ? 'border-[1.5px] border-brand-primary bg-brand-primary/[0.04]'
            : 'border border-neutral-200 bg-white hover:bg-neutral-100'
        }`}
      >
        <Icon className={`w-[18px] h-[18px] shrink-0 ${selected ? 'text-brand-primary' : 'text-neutral-600'}`} />
        <span className="text-[14px] font-medium text-brand-black">{label}</span>
        {selected && <Check className="w-[18px] h-[18px] ml-auto text-brand-primary" strokeWidth={2.5} />}
      </button>
    );
  };

  const card = (
    <div
      className={
        isDesktop
          ? 'w-[320px] bg-white rounded-lg shadow-lg p-4'
          : 'w-full bg-white rounded-t-lg shadow-lg px-4 pt-2 pb-6'
      }
      onClick={(e) => e.stopPropagation()}
    >
      {!isDesktop && (
        <div className="w-9 h-1 rounded-full bg-neutral-200 mx-auto mb-3.5" />
      )}
      <p className="text-[15px] font-semibold text-brand-black">Descargar imagen</p>
      <div className="flex flex-col gap-2 mt-3">
        {option('con', 'Con marca de agua', Stamp)}
        {option('sin', 'Sin marca de agua', ImageIcon)}
      </div>
      <p className="text-[12px] text-neutral-500 mt-2.5">
        Recordamos tu última elección para la próxima.
      </p>
    </div>
  );

  return createPortal(
    <div
      className={`fixed inset-0 z-[220] bg-brand-black/50 backdrop-blur-sm flex ${
        isDesktop ? 'items-center justify-center p-6' : 'items-end'
      }`}
      onClick={onClose}
    >
      {card}
    </div>,
    document.body,
  );
}
